import * as React from 'react'
import { type HTMLMotionProps, type Variants, motion } from 'framer-motion'
import { cn } from '@/lib/utils'

/*
 * Adapted from 21st.dev's "cta-section-with-gallery". Changes from the
 * original: framer-motion instead of motion/react, which is the same API and
 * what the rest of the site already loads, and six staggered cells instead of
 * four, one per service.
 */

interface GalleryGridCellProps extends HTMLMotionProps<'div'> {
  index: number
}

const SPRING_TRANSITION_CONFIG = {
  type: 'spring',
  stiffness: 100,
  damping: 16,
  mass: 0.75,
  restDelta: 0.005,
} as const

const filterVariants: Variants = {
  hidden: { opacity: 0, filter: 'blur(10px)' },
  visible: { opacity: 1, filter: 'blur(0px)' },
}

/*
 * Two columns offset by one short row, so the cards read as a staggered
 * wall: the right column starts at the top, the left one a step lower.
 */
const areaClasses = [
  'col-start-2 col-end-3 row-start-1 row-end-3',
  'col-start-1 col-end-2 row-start-2 row-end-4',
  'col-start-2 col-end-3 row-start-3 row-end-5',
  'col-start-1 col-end-2 row-start-4 row-end-6',
  'col-start-2 col-end-3 row-start-5 row-end-7',
  'col-start-1 col-end-2 row-start-6 row-end-8',
]

export const ContainerStagger = React.forwardRef<HTMLDivElement, HTMLMotionProps<'div'>>(
  ({ transition, ...props }, ref) => (
    <motion.div
      ref={ref}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      transition={{
        staggerChildren: transition?.staggerChildren ?? 0.2,
        delayChildren: transition?.delayChildren ?? 0.2,
        duration: 0.3,
        ...transition,
      }}
      {...props}
    />
  ),
)
ContainerStagger.displayName = 'ContainerStagger'

export const ContainerAnimated = React.forwardRef<HTMLDivElement, HTMLMotionProps<'div'>>(
  ({ transition, ...props }, ref) => (
    <motion.div
      ref={ref}
      variants={filterVariants}
      transition={{ ...SPRING_TRANSITION_CONFIG, duration: 0.3, ...transition }}
      {...props}
    />
  ),
)
ContainerAnimated.displayName = 'ContainerAnimated'

export const GalleryGrid = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'grid grid-cols-2 gap-3 sm:gap-4 grid-rows-[24px_140px_24px_140px_24px_140px_24px] sm:grid-rows-[40px_170px_40px_170px_40px_170px_40px]',
        className,
      )}
      {...props}
    />
  ),
)
GalleryGrid.displayName = 'GalleryGrid'

export const GalleryGridCell = React.forwardRef<HTMLDivElement, GalleryGridCellProps>(
  ({ className, transition, index, ...props }, ref) => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{
        duration: 0.3,
        delay: index * 0.2,
        delayChildren: transition?.delayChildren ?? 0.2,
      }}
      className={cn('relative overflow-hidden rounded-xl shadow-xl', areaClasses[index], className)}
      {...props}
    />
  ),
)
GalleryGridCell.displayName = 'GalleryGridCell'
